// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import {
  NewChargeFormData,
  newChargeFormSchema,
} from '@/billing/schemas/new-charge-form-schema';
import { parseFormAmount } from '@/billing/utilities/line-item-helpers';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { FormTextField } from '@/components/form-text-field';
import { useToast } from '@/contexts/toast-context';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { getItem } from '@/items/api/get-item';
import { ItemSearch } from '@/items/components/item-search';
import {
  SKU_PRICING_DEFAULT_ERROR_TOAST_MESSAGE,
  SKU_PRICING_DEFAULT_TOAST_DURATION_MS,
} from '@/items/constants/pricing-default-toast';
import { applyInvoiceLineItemPricingDefault } from '@/items/utilities/item-pricing-prefill';
import { LocationSearch } from '@/location/components/location-search';
import {
  TOGGLE_BUTTON_GROUP_SX,
  TOGGLE_BUTTON_SX,
} from '@/project/components/billing-plan-constants';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { DateTime } from 'luxon';
import { Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

interface NewChargeFormProps {
  formId: string;
  currencyCode: IsoCurrencyCode;
  projectStart: DateTime | null;
  projectEnd: DateTime | null;
  // Surfaced from the invoice's owning project. When set, the location
  // picker groups by customer and pins this customer's locations to the
  // top of the dropdown — ad-hoc charges almost always land at a site
  // the project's customer owns.
  priorityCustomerId: string | null;
  isOpen: boolean;
  isDisabled: boolean;
  onSubmit: (data: NewChargeFormData) => void;
  // Reports react-hook-form's isDirty up to the parent dialog so it can
  // trigger an unsaved-changes blocker on close.
  onDirtyChange?: (isDirty: boolean) => void;
}

function getDefaultValues(
  projectStartISO: string | null,
  projectEndISO: string | null,
  currencyCode: IsoCurrencyCode
): NewChargeFormData {
  return {
    item: null,
    quantity: '1',
    pricingMode: 'PER_UNIT',
    unitPrice: { amount: '', isoCode: currencyCode },
    totalPrice: { amount: '', isoCode: currencyCode },
    location: null,
    description: '',
    servicePeriod: [
      projectStartISO ? DateTime.fromISO(projectStartISO) : null,
      projectEndISO ? DateTime.fromISO(projectEndISO) : null,
    ],
    expectedBillableDate: DateTime.now().startOf('day'),
    taxAmount: { amount: '', isoCode: currencyCode },
  };
}

export function NewChargeForm(props: NewChargeFormProps) {
  const projectStartISO = props.projectStart?.toISODate() ?? null;
  const projectEndISO = props.projectEnd?.toISODate() ?? null;
  const currency = props.currencyCode;

  const { control, getValues, handleSubmit, reset, setValue, formState } =
    useForm<NewChargeFormData>({
      resolver: zodResolver(newChargeFormSchema),
      defaultValues: getDefaultValues(projectStartISO, projectEndISO, currency),
    });
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const pricingPrefillRequestRef = useRef(0);
  const isOpenRef = useRef(props.isOpen);
  isOpenRef.current = props.isOpen;

  // Reset only when the dialog opens (false → true) so a background
  // refetch can't wipe the form mid-edit.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    if (props.isOpen && !wasOpen) {
      pricingPrefillRequestRef.current += 1;
      reset(getDefaultValues(projectStartISO, projectEndISO, currency));
    }
    if (!props.isOpen && wasOpen) {
      pricingPrefillRequestRef.current += 1;
    }
    wasOpenRef.current = props.isOpen;
  }, [props.isOpen, projectStartISO, projectEndISO, currency, reset]);

  const onDirtyChange = props.onDirtyChange;
  useEffect(() => {
    onDirtyChange?.(formState.isDirty);
  }, [formState.isDirty, onDirtyChange]);

  const pricingMode = useWatch({ control, name: 'pricingMode' });
  const quantity = useWatch({ control, name: 'quantity' });
  const unitPrice = useWatch({ control, name: 'unitPrice' });
  const totalPrice = useWatch({ control, name: 'totalPrice' });
  const taxAmount = useWatch({ control, name: 'taxAmount' });
  const computedAmount = useMemo(
    () =>
      pricingMode === 'PER_UNIT'
        ? parseFormAmount(quantity).times(parseFormAmount(unitPrice?.amount))
        : parseFormAmount(totalPrice?.amount),
    [pricingMode, quantity, unitPrice, totalPrice]
  );
  const computedLineItemTotal = useMemo(
    () => computedAmount.plus(parseFormAmount(taxAmount?.amount)),
    [computedAmount, taxAmount]
  );

  const applySelectedItemPricingDefault = useCallback(
    async (itemKey: string, requestId: number) => {
      const isCurrentRequest = () =>
        pricingPrefillRequestRef.current === requestId &&
        isOpenRef.current &&
        getValues('item')?.key === itemKey;

      try {
        const { item } = await queryClient.fetchQuery({
          queryKey: ['item', itemKey],
          queryFn: () => getItem(itemKey),
        });

        if (!isCurrentRequest()) return;

        const currentUnitPrice = getValues('unitPrice').amount;
        const currentTotalPrice = getValues('totalPrice').amount;
        const currentPricing = {
          pricingMode: getValues('pricingMode'),
          unitPrice: currentUnitPrice === '' ? null : currentUnitPrice,
          totalPrice: currentTotalPrice,
        };
        const nextPricing = applyInvoiceLineItemPricingDefault(
          currentPricing,
          item.defaultPricing,
          currency
        );

        if (nextPricing.pricingMode !== currentPricing.pricingMode) {
          setValue('pricingMode', nextPricing.pricingMode, {
            shouldDirty: true,
          });
        }
        if (nextPricing.unitPrice !== currentPricing.unitPrice) {
          setValue('unitPrice.amount', nextPricing.unitPrice ?? '', {
            shouldDirty: true,
          });
        }
        if (nextPricing.totalPrice !== currentPricing.totalPrice) {
          setValue('totalPrice.amount', nextPricing.totalPrice, {
            shouldDirty: true,
          });
        }
      } catch (error) {
        if (!isCurrentRequest()) return;

        if (axios.isAxiosError(error)) {
          const apiError = handleAPIError(error);
          if (!apiError.sentToSentry) {
            captureException(error);
          }
        } else {
          captureException(error);
        }
        showToast({
          severity: 'error',
          element: <span>{SKU_PRICING_DEFAULT_ERROR_TOAST_MESSAGE}</span>,
          duration: SKU_PRICING_DEFAULT_TOAST_DURATION_MS,
        });
      }
    },
    [currency, getValues, queryClient, setValue, showToast]
  );

  return (
    <form id={props.formId} onSubmit={handleSubmit(props.onSubmit)} noValidate>
      <Stack spacing={2.25}>
        <Controller
          name="item"
          control={control}
          render={({ field, fieldState }) => (
            <ItemSearch
              label="Item"
              value={field.value ?? null}
              onChange={(value: SparseItem | null) => {
                pricingPrefillRequestRef.current += 1;
                const requestId = pricingPrefillRequestRef.current;
                field.onChange(value);
                if (value !== null) {
                  setValue('description', value.name);
                  void applySelectedItemPricingDefault(value.key, requestId);
                } else {
                  setValue('pricingMode', 'PER_UNIT', { shouldDirty: true });
                  setValue('unitPrice.amount', '', { shouldDirty: true });
                  setValue('totalPrice.amount', '', { shouldDirty: true });
                }
              }}
              disabled={props.isDisabled}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 3 }}>
              <FormNumberTextField
                name="quantity"
                control={control}
                label="Quantity"
                disabled={props.isDisabled}
              />
            </Box>
            <Box sx={{ flex: 3 }}>
              {pricingMode === 'PER_UNIT' ? (
                <FormCurrencyTextField
                  key="unitPrice"
                  name="unitPrice"
                  control={control}
                  label="Unit price"
                  disabled={props.isDisabled}
                  lockTo={currency}
                  compact
                  hideRightAdornment
                  fullWidth
                />
              ) : (
                <FormCurrencyTextField
                  key="totalPrice"
                  name="totalPrice"
                  control={control}
                  label="Total price"
                  disabled={props.isDisabled}
                  lockTo={currency}
                  compact
                  hideRightAdornment
                  fullWidth
                />
              )}
            </Box>
            <Controller
              name="pricingMode"
              control={control}
              render={({ field }) => (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={field.value}
                  onChange={(_event, next) => {
                    if (next === null) return;
                    field.onChange(next);
                  }}
                  sx={{ ...TOGGLE_BUTTON_GROUP_SX, flex: 4, width: '100%' }}
                >
                  <ToggleButton
                    value="PER_UNIT"
                    sx={{ ...TOGGLE_BUTTON_SX, flex: 1 }}
                  >
                    Per unit
                  </ToggleButton>
                  <ToggleButton
                    value="FIXED"
                    sx={{ ...TOGGLE_BUTTON_SX, flex: 1 }}
                  >
                    Fixed
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ textAlign: 'right', fontWeight: 'bold' }}
          >
            Amount:{' '}
            {formatAmountWithCurrency(computedAmount, props.currencyCode)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 3 }} />
            <Box sx={{ flex: 3 }} />
            <Box sx={{ flex: 4 }}>
              <FormCurrencyTextField
                name="taxAmount"
                control={control}
                label="Tax"
                placeholder="(optional)"
                disabled={props.isDisabled}
                lockTo={currency}
                compact
                hideRightAdornment
                fullWidth
              />
            </Box>
          </Box>
          <Typography
            variant="body2"
            sx={{ textAlign: 'right', fontWeight: 'bold' }}
          >
            Line total:{' '}
            {formatAmountWithCurrency(
              computedLineItemTotal,
              props.currencyCode
            )}
          </Typography>
        </Stack>
        {props.projectStart !== null && props.projectEnd !== null && (
          <Alert severity="info">
            Project runs from{' '}
            {props.projectStart.toLocaleString(DateTime.DATE_MED)} to{' '}
            {props.projectEnd.toLocaleString(DateTime.DATE_MED)}
          </Alert>
        )}
        <FormTextField
          name="description"
          control={control}
          label="Description"
          disabled={props.isDisabled}
          fullWidth
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Suspense fallback={null}>
            <FormDatePicker
              name="servicePeriod.0"
              control={control}
              label="Service period start"
              isDisabled={props.isDisabled}
              isClearable
            />
          </Suspense>
          <Suspense fallback={null}>
            <FormDatePicker
              name="servicePeriod.1"
              control={control}
              label="Service period end"
              isDisabled={props.isDisabled}
              isClearable
            />
          </Suspense>
        </Box>

        <Suspense fallback={null}>
          <FormDatePicker
            name="expectedBillableDate"
            control={control}
            label="Expected billable date"
            isDisabled={props.isDisabled}
            isClearable
          />
        </Suspense>
        <Controller
          name="location"
          control={control}
          render={({ field, fieldState }) => (
            <LocationSearch
              label="Location (optional)"
              value={field.value ?? null}
              onChange={(value: SparseLocation | null) => {
                field.onChange(value);
              }}
              onCreate={() => {
                // Inline location creation isn't supported here.
              }}
              grouping={
                props.priorityCustomerId !== null ? ['customer'] : undefined
              }
              priorityCustomerId={props.priorityCustomerId ?? undefined}
              disabled={props.isDisabled}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Stack>
    </form>
  );
}
